import React, { useState, useEffect, useCallback } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseEther, formatEther } from 'viem';
import { Wallet, ExternalLink, AlertCircle, Check, Loader2, ArrowUpDown, Settings, Info } from 'lucide-react';
import { useTokenBalance } from '../../hooks/useTokenBalance';
import { usePriceQuote, useTokenStatus } from '../../hooks/useSwap';
import { BONDING_CURVE_ROUTER_ABI, NADFUN_CONTRACTS, ERC20_ABI } from '../../config/abi';
import { NADFUN_TOKEN_URL } from '../../config/constants';

// Token addresses
const DUCK_TOKEN_ADDRESS = (import.meta.env.VITE_DUCK_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`;

const SwapInterface: React.FC = () => {
    const { isConnected, address } = useAccount();
    const { mon, duck } = useTokenBalance();
    const { isGraduated, isLocked, progressPercent } = useTokenStatus();

    const [inputAmount, setInputAmount] = useState('');
    const [slippage, setSlippage] = useState('10');
    const [showSettings, setShowSettings] = useState(false);
    const [isBuyMode, setIsBuyMode] = useState(true); // true = Buy DUCK, false = Sell DUCK

    // Get price quote from Lens (direction based on mode)
    const {
        amountOut,
        amountOutFormatted,
        isLoading: isQuoteLoading,
        refetch: refetchQuote
    } = usePriceQuote(inputAmount, isBuyMode);

    // Write contract hooks
    const {
        writeContract,
        data: txHash,
        isPending: isWritePending,
        error: writeError,
        reset: resetWrite
    } = useWriteContract();

    // Approval for selling
    const {
        writeContract: writeApprove,
        data: approveTxHash,
        isPending: isApprovePending,
    } = useWriteContract();

    // Wait for transaction confirmation
    const {
        isLoading: isConfirming,
        isSuccess: isConfirmed,
    } = useWaitForTransactionReceipt({
        hash: txHash,
    });

    // Wait for approval confirmation
    const {
        isLoading: isApproveConfirming,
        isSuccess: isApproveConfirmed,
    } = useWaitForTransactionReceipt({
        hash: approveTxHash,
    });

    // Refetch after successful tx
    useEffect(() => {
        if (isConfirmed) {
            refetchQuote();
            setInputAmount('');
        }
    }, [isConfirmed, refetchQuote]);

    // Toggle swap direction
    const toggleDirection = () => {
        setIsBuyMode(!isBuyMode);
        setInputAmount('');
    };

    // Get current balances based on mode
    const fromBalance = isBuyMode ? mon.balance : duck.balance;
    const fromSymbol = isBuyMode ? 'MON' : 'DUCK';
    const toSymbol = isBuyMode ? 'DUCK' : 'MON';
    const fromLogo = isBuyMode ? '/images/mon.svg' : '/images/duck.png';
    const toLogo = isBuyMode ? '/images/duck.png' : '/images/mon.svg';

    // Handle buy execution
    const handleBuy = useCallback(() => {
        if (!address || !inputAmount || parseFloat(inputAmount) <= 0) return;
        if (amountOut === 0n) return;

        const deadline = Math.floor(Date.now() / 1000) + 300;
        const amountInWei = parseEther(inputAmount);
        const slippageMultiplier = 100 - parseInt(slippage);
        const minOut = (amountOut * BigInt(slippageMultiplier)) / 100n;

        writeContract({
            address: NADFUN_CONTRACTS.BONDING_CURVE_ROUTER as `0x${string}`,
            abi: BONDING_CURVE_ROUTER_ABI,
            functionName: 'buy',
            args: [{
                amountOutMin: minOut,
                token: DUCK_TOKEN_ADDRESS,
                to: address,
                deadline: BigInt(deadline),
            }],
            value: amountInWei,
        });
    }, [address, inputAmount, amountOut, slippage, writeContract]);

    // Handle sell execution
    const handleSell = useCallback(async () => {
        if (!address || !inputAmount || parseFloat(inputAmount) <= 0) return;
        if (amountOut === 0n) return;

        const amountInWei = parseEther(inputAmount);

        // First approve if needed
        writeApprove({
            address: DUCK_TOKEN_ADDRESS,
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [NADFUN_CONTRACTS.BONDING_CURVE_ROUTER as `0x${string}`, amountInWei],
        });
    }, [address, inputAmount, amountOut, writeApprove]);

    // Execute sell after approval
    useEffect(() => {
        if (isApproveConfirmed && !isBuyMode && inputAmount && amountOut > 0n) {
            const deadline = Math.floor(Date.now() / 1000) + 300;
            const amountInWei = parseEther(inputAmount);
            const slippageMultiplier = 100 - parseInt(slippage);
            const minOut = (amountOut * BigInt(slippageMultiplier)) / 100n;

            writeContract({
                address: NADFUN_CONTRACTS.BONDING_CURVE_ROUTER as `0x${string}`,
                abi: BONDING_CURVE_ROUTER_ABI,
                functionName: 'sell',
                args: [{
                    amountIn: amountInWei,
                    amountOutMin: minOut,
                    token: DUCK_TOKEN_ADDRESS,
                    to: address!,
                    deadline: BigInt(deadline),
                }],
            });
        }
    }, [isApproveConfirmed, isBuyMode, inputAmount, amountOut, slippage, address, writeContract]);

    // Handle swap execution
    const handleSwap = () => {
        if (isBuyMode) {
            handleBuy();
        } else {
            handleSell();
        }
    };

    // Preset amounts
    const presetAmounts = isBuyMode
        ? ['0.01', '0.05', '0.1', '0.5']
        : ['100', '1000', '10000', '50000'];

    // Check if can swap
    const canSwap = isConnected &&
        inputAmount &&
        parseFloat(inputAmount) > 0 &&
        parseFloat(inputAmount) <= parseFloat(fromBalance) &&
        !isWritePending &&
        !isConfirming &&
        !isApprovePending &&
        !isApproveConfirming &&
        !isLocked;

    // Get button text
    const getButtonText = () => {
        if (!isConnected) return 'Connect Wallet';
        if (isApprovePending || isApproveConfirming) return 'Approving...';
        if (isWritePending) return 'Confirm in Wallet...';
        if (isConfirming) return 'Confirming...';
        if (isLocked) return '🔒 Token Locked';
        if (!inputAmount || parseFloat(inputAmount) <= 0) return 'Enter Amount';
        if (parseFloat(inputAmount) > parseFloat(fromBalance)) return `Insufficient ${fromSymbol}`;
        return isBuyMode ? '🦆 Buy DUCK' : '💰 Sell DUCK';
    };

    // Not connected view
    if (!isConnected) {
        return (
            <section id="swap" className="section swap-section">
                <div className="swap-wrapper">
                    <div className="swap-card">
                        <div className="swap-header">
                            <h2>Swap</h2>
                        </div>
                        <div className="swap-connect-state">
                            <Wallet size={48} strokeWidth={1.5} />
                            <h3>Connect Wallet</h3>
                            <p>Connect your wallet to swap MON for DUCK</p>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="swap" className="section swap-section">
            <div className="swap-wrapper">
                <div className="swap-card">
                    {/* Header */}
                    <div className="swap-header">
                        <h2>{isBuyMode ? 'Buy DUCK' : 'Sell DUCK'}</h2>
                        <button
                            className="swap-settings-btn"
                            onClick={() => setShowSettings(!showSettings)}
                        >
                            <Settings size={20} />
                        </button>
                    </div>

                    {/* Settings Panel */}
                    {showSettings && (
                        <div className="swap-settings-panel">
                            <div className="settings-row">
                                <span>Slippage Tolerance</span>
                                <div className="slippage-options">
                                    {['5', '10', '15', '20'].map((s) => (
                                        <button
                                            key={s}
                                            className={`slippage-option ${slippage === s ? 'active' : ''}`}
                                            onClick={() => setSlippage(s)}
                                        >
                                            {s}%
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Progress Bar */}
                    {!isGraduated && progressPercent > 0 && (
                        <div className="bonding-progress">
                            <div className="progress-info">
                                <span>Bonding Curve</span>
                                <span>{progressPercent.toFixed(1)}%</span>
                            </div>
                            <div className="progress-track">
                                <div className="progress-bar" style={{ width: `${Math.min(progressPercent, 100)}%` }} />
                            </div>
                        </div>
                    )}

                    {/* From Token */}
                    <div className="swap-token-box">
                        <div className="token-box-header">
                            <span className="token-label">You pay</span>
                            <span className="token-balance" onClick={() => setInputAmount(fromBalance)}>
                                Balance: {isBuyMode ? parseFloat(fromBalance).toFixed(4) : parseFloat(fromBalance).toLocaleString()}
                            </span>
                        </div>
                        <div className="token-input-row">
                            <input
                                type="number"
                                value={inputAmount}
                                onChange={(e) => setInputAmount(e.target.value)}
                                placeholder="0"
                                className="token-amount-input"
                                min="0"
                                step={isBuyMode ? "0.01" : "100"}
                            />
                            <div className={`token-selector ${!isBuyMode ? 'duck-selector' : ''}`}>
                                <img src={fromLogo} alt={fromSymbol} className="token-logo" />
                                <span className="token-symbol">{fromSymbol}</span>
                            </div>
                        </div>
                        <div className="quick-amounts">
                            {presetAmounts.map((amt) => (
                                <button key={amt} className="quick-amount-btn" onClick={() => setInputAmount(amt)}>
                                    {isBuyMode ? amt : parseInt(amt).toLocaleString()}
                                </button>
                            ))}
                            <button
                                className="quick-amount-btn max-btn"
                                onClick={() => setInputAmount(isBuyMode ? (parseFloat(fromBalance) * 0.99).toFixed(6) : fromBalance)}
                            >
                                MAX
                            </button>
                        </div>
                    </div>

                    {/* Swap Direction Toggle */}
                    <div className="swap-arrow-container">
                        <button className="swap-arrow-circle" onClick={toggleDirection} title="Switch direction">
                            <ArrowUpDown size={20} />
                        </button>
                    </div>

                    {/* To Token */}
                    <div className="swap-token-box receive-box">
                        <div className="token-box-header">
                            <span className="token-label">You receive</span>
                            <span className="token-balance">
                                Balance: {isBuyMode ? parseFloat(duck.balance).toLocaleString() : parseFloat(mon.balance).toFixed(4)}
                            </span>
                        </div>
                        <div className="token-input-row">
                            <div className="token-amount-display">
                                {isQuoteLoading ? (
                                    <Loader2 size={24} className="spin" />
                                ) : (
                                    <span>
                                        {amountOutFormatted !== '0'
                                            ? (isBuyMode
                                                ? parseFloat(amountOutFormatted).toLocaleString(undefined, { maximumFractionDigits: 0 })
                                                : parseFloat(amountOutFormatted).toFixed(4)
                                            )
                                            : '0'
                                        }
                                    </span>
                                )}
                            </div>
                            <div className={`token-selector ${isBuyMode ? 'duck-selector' : ''}`}>
                                <img src={toLogo} alt={toSymbol} className="token-logo" />
                                <span className="token-symbol">{toSymbol}</span>
                            </div>
                        </div>
                    </div>

                    {/* Trade Details */}
                    {inputAmount && parseFloat(inputAmount) > 0 && amountOut > 0n && (
                        <div className="trade-details">
                            <div className="trade-detail-row">
                                <span><Info size={14} /> Rate</span>
                                <span>
                                    1 {fromSymbol} = {(parseFloat(amountOutFormatted) / parseFloat(inputAmount)).toLocaleString(undefined, { maximumFractionDigits: isBuyMode ? 0 : 4 })} {toSymbol}
                                </span>
                            </div>
                            <div className="trade-detail-row">
                                <span>Slippage</span>
                                <span>{slippage}%</span>
                            </div>
                            <div className="trade-detail-row">
                                <span>Min. received</span>
                                <span>
                                    {parseFloat(formatEther((amountOut * BigInt(100 - parseInt(slippage))) / 100n)).toLocaleString(undefined, { maximumFractionDigits: isBuyMode ? 0 : 4 })} {toSymbol}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {writeError && (
                        <div className="swap-message error">
                            <AlertCircle size={16} />
                            <span>{writeError.message.slice(0, 80)}...</span>
                            <button onClick={() => resetWrite()}>×</button>
                        </div>
                    )}

                    {/* Success Message */}
                    {isConfirmed && (
                        <div className="swap-message success">
                            <Check size={16} />
                            <span>{isBuyMode ? 'Purchase' : 'Sale'} successful! 🦆</span>
                            <a href={`https://monadvision.com/tx/${txHash}`} target="_blank" rel="noreferrer">
                                View <ExternalLink size={12} />
                            </a>
                        </div>
                    )}

                    {/* Swap Button */}
                    <button
                        className={`swap-execute-btn ${canSwap ? 'active' : 'disabled'}`}
                        onClick={handleSwap}
                        disabled={!canSwap}
                    >
                        {(isWritePending || isConfirming || isApprovePending || isApproveConfirming) && (
                            <Loader2 size={20} className="spin" />
                        )}
                        {getButtonText()}
                    </button>

                    {/* Alternative Link */}
                    <a href={NADFUN_TOKEN_URL} target="_blank" rel="noreferrer" className="swap-alt-link">
                        Trade on Nad.fun <ExternalLink size={12} />
                    </a>
                </div>

                {/* Balances Card */}
                <div className="balances-card">
                    <h3>Your Balances</h3>
                    <div className="balance-row">
                        <div className="balance-token">
                            <img src="/images/mon.svg" alt="MON" className="balance-token-logo" />
                            <span>MON</span>
                        </div>
                        <span className="balance-amount">{parseFloat(mon.balance).toFixed(4)}</span>
                    </div>
                    <div className="balance-row">
                        <div className="balance-token">
                            <img src="/images/duck.png" alt="DUCK" className="balance-token-logo" />
                            <span>DUCK</span>
                        </div>
                        <span className="balance-amount duck">{parseFloat(duck.balance).toLocaleString()}</span>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SwapInterface;
